import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function resizeImage(file: File, maxWidth: number = 1000): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height *= ratio;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not create canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        canvas.toBlob(blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Could not convert canvas to blob'));
          }
        }, file.type);
      };

      img.onerror = () => reject(new Error('Error loading image'));
      img.src = event.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Error reading file'));
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function truncateText(text: string, maxLength: number = 50): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function formatTranscribedText(text: string): string {
  // Remove extra whitespace
  let formatted = text.trim().replace(/\s+/g, ' ');

  // Split into sentences
  const sentences = formatted.split(/([.!?]+\s+)/).filter(Boolean);

  // Capitalize first letter of each sentence and trim
  formatted = sentences
    .map(sentence => {
      const trimmed = sentence.trim();
      return trimmed ? capitalizeFirstLetter(trimmed) : trimmed;
    })
    .join(' ');

  // Ensure first letter is capitalized
  return capitalizeFirstLetter(formatted);
}

export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

export function debounce<F extends (...args: any[]) => any>(func: F, delay: number): F {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (this: any, ...args: Parameters<F>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  } as F;
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

export function formatDate(date: Date | string, format: 'short' | 'long' = 'short'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (format === 'short') {
    return dateObj.toLocaleDateString();
  }

  return dateObj.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// New retry functionality for handling connection issues
export async function withRetry<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return withRetry(fn, retries - 1, delay * 2);
  }
}

export function getAvatarUrl(baseUrl: string | null, updatedAt: string | null): string | null {
  if (!baseUrl) return null;

  // If we have an updated timestamp, use it for cache busting
  if (updatedAt) {
    const timestamp = new Date(updatedAt).getTime();
    const url = new URL(baseUrl);
    url.searchParams.set('v', timestamp.toString());
    return url.toString();
  }

  return baseUrl;
}

/**
 * @deprecated Use useLoadingScreenStore.hideLoadingScreen() instead
 * This function will be removed in a future version. Use the loading screen store for
 * more comprehensive loading screen management.
 */
export function hideGlobalLoadingScreen(): void {
  // Direct DOM manipulation without dynamic imports
  const loader = document.getElementById('app-loading');
  if (loader) {
    loader.classList.add('hide');
    setTimeout(() => {
      if (loader.parentNode) {
        loader.parentNode.removeChild(loader);
      }
    }, 300);
  }
}

// Path preservation functions to handle page refreshes
export const pathPreservation = {
  // Save the current path for restoration after refresh
  savePath: (path: string, search: string = ''): void => {
    if (path && path !== '/' && !path.includes('/auth')) {
      // Store the full URL including search params
      const fullPath = search ? `${path}${search}` : path;
      localStorage.setItem('lastPath', fullPath);
      console.log('Saved path for restoration:', fullPath);
    }
  },

  // Get the saved path
  getSavedPath: (): string | null => {
    return localStorage.getItem('lastPath');
  },

  // Clear the saved path
  clearSavedPath: (): void => {
    localStorage.removeItem('lastPath');
  },

  // Determine if we should restore a path
  shouldRestorePath: (currentPath: string): boolean => {
    const savedPath = localStorage.getItem('lastPath');
    // Return true if we have a saved path that isn't the landing page
    // and we're currently on the landing page
    return Boolean(savedPath && !savedPath.startsWith('/auth') && currentPath === '/');
  },
};

// Calculate optimal position for popups to ensure they stay within viewport
/**
 * Formats a user display name to ensure consistent capitalization:
 * - First letter of each word is capitalized
 * - Rest of the letters in each word are lowercase
 * - Handles null, undefined, or empty strings gracefully
 *
 * @param displayName The user display name to format
 * @param fallback Optional fallback text if displayName is empty
 * @returns Properly formatted display name
 */
export function formatUserDisplayName(
  displayName: string | null | undefined,
  fallback: string = 'Unknown User'
): string {
  // Handle null, undefined or empty string
  if (!displayName) return fallback;

  displayName = displayName.trim();
  if (!displayName) return fallback;

  // Split the name by spaces and format each word
  return displayName
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const calculateOptimalPopupPosition = (
  triggerPosition: { top: number; left: number },
  popupDimensions: { width: number; height: number },
  offset: { x: number; y: number } = { x: 0, y: 30 }
): { top: number; left: number } => {
  // Get viewport dimensions - use clientWidth/clientHeight for cross-browser compatibility
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;

  // Calculate initial positions with offset
  let top = triggerPosition.top + offset.y;
  let left = triggerPosition.left + offset.x;

  // Check if popup would go off the bottom of the screen
  if (top + popupDimensions.height > viewportHeight) {
    // Position above the trigger instead
    top = triggerPosition.top - popupDimensions.height - 10;

    // If still off-screen (not enough space above), position at the bottom of the viewport with some padding
    if (top < 0) {
      top = Math.max(10, viewportHeight - popupDimensions.height - 10);
    }
  }

  // Check if popup would go off the right of the screen
  if (left + popupDimensions.width > viewportWidth) {
    // Position to the left
    left = Math.max(10, viewportWidth - popupDimensions.width - 10);
  }

  // Check if popup would go off the left of the screen
  if (left < 0) {
    left = 10; // Add some padding
  }

  return { top, left };
};
